import React, { useEffect, useMemo, useRef, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty, set, concat } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as sampleTodoListAction from '../Samples/store/TodoList.action';
import { Year, createOptions, checkArray, Page_Size, getYears } from '../../../../Components/helper';
import { fetchColorLibrary, fetchColorGroupLibrary } from '../../../Library/ColorLibary/store/ColorLibrary.action';
import { getSupplierBrandsAction } from '../Costing/store/Costing.action';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import { getDefaultSampleStatusIds } from '../Samples/default-sample-status-filter';
import { CurrentYearItemRenderer } from '../../../../Components/Utility/CurrentYearDropdown';

function FilterSampleTodoList() {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { t: tReports } = useTranslation('todo-reports');

  const reducers = useSelector((redState) => redState);
  const {
    UserDetails: {
      userInfo: { userId },
      isSupplierLoggedIn,
      isCompanyUserLoggedIn,
    },
    SystemMastersDetails: { multiSubBrandList, pcTypeList, licensorList, licenseList, likenessList },
    ColorLibraryDetails: { ColorLibGroup: colorLibList },
    SampleTodoListData: {
      filterValues: {
        searchText,
        selectedSeason,
        selectedYear,
        selectedBrand,
        selectedSubBrand,
        selectedSupplier,
        selectedSampleType,
        selectedColour,
        selectedColourGroup,
        selectedTrackingStatus,
        selectedSampleStatus,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedProductType,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
        sortOrder,
        sortPropertyName,
      },
    },
  } = reducers;

  const [state, setState] = useState({
    seasonOptions: [],
    yearOptions: createOptions(getYears(), 'name', '_id'),
    brandOptions: [],
    subBrandOptions: [],
    supplierOptions: [],
    sampleTypeOptions: [],
    colourOptions: [],
    colorGroupOptions: [],
    sampleStatusOptions: [],
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    trackingStatusOptions: [],
    productTypeOptions: [],
  });
  const {
    seasonOptions,
    yearOptions,
    brandOptions,
    subBrandOptions,
    supplierOptions,
    sampleTypeOptions,
    colourOptions,
    colorGroupOptions,
    sampleStatusOptions,
    category1Options,
    category2Options,
    category3Options,
    category4Options,
    trackingStatusOptions,
    productTypeOptions,
  } = state;

  /** Clearing the filters returns the Sample Status selection to its default rather than to none. */
  const defaultSampleStatusIds = useMemo(() => getDefaultSampleStatusIds(sampleStatusOptions), [sampleStatusOptions]);

  /**
   * Drives the filter icon's active state. Derived from the live selections rather than from the
   * saved localStorage snapshot so that any applied filter indicates — including the grey/amber
   * Sample Status default, which is applied without ever being saved.
   */
  const isApply = useMemo(
    () =>
      [
        selectedSeason,
        selectedYear,
        selectedBrand,
        selectedSubBrand,
        selectedSupplier,
        selectedSampleType,
        selectedColour,
        selectedColourGroup,
        selectedProductType,
        selectedTrackingStatus,
        selectedSampleStatus,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
        selectedLicensor,
        selectedLicense,
        selectedLikeness,
      ].some((selection) => Array.isArray(selection) && selection.length > 0),
    [
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType,
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    ],
  );
  const [subBrandAccess, setSubBrandAccess] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);
  const [licensorAccess, setLicensorAccess] = useState(false);
  // prevent re-entrant fetches
  const isFetchingCatsRef = useRef(false);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
      setSubBrandAccess(subBrandAccessRes?.data || false);
    };
    fetchSubBrandAccess();
  }, [dispatch]);

  useEffect(() => {
    if (!userId) return;
    const fetchLicensorSetup = async () => {
      const res = await dispatch(systemMasterAction.getLicensorAccess()).catch(() => null);
      const hasAccess = !!res?.isActiveLicensor;
      setLicensorAccess(hasAccess);
      if (hasAccess) {
        dispatch(systemMasterAction.getLicensorDetails({ loggedInUserId: userId, isActive: true }));
      }
    };
    fetchLicensorSetup();
  }, [dispatch, userId]);

  useEffect(() => {
    if (!userId) return;
    const saved = localStorage.getItem('sampleTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      if (parsed?.selectedLicensor?.length > 0) {
        dispatch(
          systemMasterAction.getLicenseDetails({
            licensorIds: parsed.selectedLicensor,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
      if (parsed?.selectedLicensor?.length > 0 && parsed?.selectedLicense?.length > 0) {
        dispatch(
          systemMasterAction.getLikenessDetails({
            licensorIds: parsed.selectedLicensor,
            licenseIds: parsed.selectedLicense,
            loggedInUserId: userId,
            isActive: true,
          }),
        );
      }
    } catch (e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  // Fetch feature flag for cascading by Product Type
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(systemMasterAction.getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  // Ensure Product Types (Tier1) are available for the Product Type MultiSelect
  useEffect(() => {
    dispatch(
      systemMasterAction.getPCTypesDetails(
        {
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        },
        true,
      ),
    );
  }, [dispatch, userId]);

  // Rehydrate saved filter selections into Redux on initial mount so dependent fetches have parent IDs
  useEffect(() => {
    const saved = localStorage.getItem('sampleTodoListFilter');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      const allEmpty = [
        selectedSeason,
        selectedYear,
        selectedBrand,
        selectedSubBrand,
        selectedSupplier,
        selectedSampleType,
        selectedColour,
        selectedColourGroup,
        selectedProductType,
        selectedTrackingStatus,
        selectedSampleStatus,
        selectedCategory1,
        selectedCategory2,
        selectedCategory3,
        selectedCategory4,
      ].every((v) => !Array.isArray(v) || v.length === 0);
      if (allEmpty) {
        dispatch(sampleTodoListAction.filterSortData(parsed));
      }
    } catch (e) {
      // ignore corrupt saved state
    }
  }, [dispatch]);

  // Fetch subbrands when brand changes
  useEffect(() => {
    const brandId = selectedBrand;
    if (brandId) {
      dispatch(
        systemMasterAction.getMultiSubBrandDetails({
          brandId: Array.isArray(brandId) ? brandId : [brandId],
        }),
      );
    }
  }, [dispatch, selectedBrand]);

  // Map reducer sub-brand list to component options whenever it updates
  useEffect(() => {
    if (Array.isArray(multiSubBrandList) && multiSubBrandList.length > 0) {
      const opts = createOptions(multiSubBrandList, 'name', '_id');
      setState((prev) => ({ ...prev, subBrandOptions: opts }));
    } else {
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
  }, [multiSubBrandList]);

  // Build product type options locally from store list
  useEffect(() => {
    if (Array.isArray(pcTypeList) && pcTypeList.length > 0) {
      setState((prev) => ({ ...prev, productTypeOptions: createOptions(pcTypeList, 'name', '_id') }));
    } else {
      setState((prev) => ({ ...prev, productTypeOptions: [] }));
    }
  }, [pcTypeList]);

  // Helpers for cascading (now that dependencies are defined)
  const getIds = (arr) => (Array.isArray(arr) ? arr.filter(Boolean).map((v) => String(v)) : []);
  const selectedPTIds = getIds(selectedProductType);
  const selectedCat1Ids = getIds(selectedCategory1);
  const selectedCat2Ids = getIds(selectedCategory2);
  const selectedCat3Ids = getIds(selectedCategory3);

  const category1Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category1Options;
    if (!selectedPTIds?.length) return category1Options;
    const base = category1Options || [];
    const filtered = base.filter((opt) => {
      const used = Array.isArray(opt?.isUsedProductType) ? opt.isUsedProductType : [];
      const pts = Array.isArray(opt?.productType) ? opt.productType.map((p) => (typeof p === 'object' ? p?._id : p)).filter(Boolean) : [];
      return used?.some((id) => selectedPTIds.includes(id)) || pts?.some((id) => selectedPTIds.includes(id));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category1Options, selectedPTIds]);

  const category2Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category2Options;
    if (!selectedCat1Ids?.length) return category2Options;
    const base = category2Options || [];
    const hasMeta = base.some((opt) => Array.isArray(opt?.isUsedCat1) && opt.isUsedCat1.length);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => {
      const usedCat1 = Array.isArray(opt?.isUsedCat1) ? opt.isUsedCat1.map((v) => String(v)) : [];
      return usedCat1?.some((id) => selectedCat1Ids.includes(String(id)));
    });
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category2Options, selectedCat1Ids]);

  const category3Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category3Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length) return category3Options;
    const base = category3Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId);
    if (!hasMeta) return base;
    const filtered = base.filter((opt) => selectedCat1Ids.includes(String(opt?.categoryOneId)) && selectedCat2Ids.includes(String(opt?.categoryTwoId)));
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category3Options, selectedCat1Ids, selectedCat2Ids]);

  const category4Filtered = useMemo(() => {
    if (!catBasedOnPTData) return category4Options;
    if (!selectedCat1Ids?.length || !selectedCat2Ids?.length || !selectedCat3Ids?.length) return category4Options;
    const base = category4Options || [];
    const hasMeta = base.some((opt) => opt?.categoryOneId && opt?.categoryTwoId && opt?.categoryThreeId);
    if (!hasMeta) return base;
    const filtered = base.filter(
      (opt) =>
        selectedCat1Ids.includes(String(opt?.categoryOneId)) &&
        selectedCat2Ids.includes(String(opt?.categoryTwoId)) &&
        selectedCat3Ids.includes(String(opt?.categoryThreeId)),
    );
    return filtered?.length ? filtered : base;
  }, [catBasedOnPTData, category4Options, selectedCat1Ids, selectedCat2Ids, selectedCat3Ids]);

  const licensorOptions = useMemo(() => (checkArray(licensorList) ? createOptions(licensorList, 'name', '_id') : []), [licensorList]);
  const licenseOptions = useMemo(() => (checkArray(licenseList) ? createOptions(licenseList, 'name', '_id') : []), [licenseList]);
  const likenessOptions = useMemo(() => (checkArray(likenessList) ? createOptions(likenessList, 'name', '_id') : []), [likenessList]);

  const handleFilterLicense = (val, key) => {
    const newData = { [key]: val };
    if (key === 'selectedLicensor') {
      newData.selectedLicense = [];
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0) {
        dispatch(systemMasterAction.getLicenseDetails({ licensorIds: val, loggedInUserId: userId, isActive: true }));
      } else {
        dispatch(systemMasterAction.clearLicenseData());
      }
    }
    if (key === 'selectedLicense') {
      newData.selectedLikeness = [];
      dispatch(systemMasterAction.clearLikenessData());
      if (val.length > 0 && checkArray(selectedLicensor)) {
        dispatch(systemMasterAction.getLikenessDetails({ licensorIds: selectedLicensor, licenseIds: val, loggedInUserId: userId, isActive: true }));
      }
    }
    dispatch(sampleTodoListAction.filterSortData(newData));
  };

  const handleSelectChange = async (value, name) => {
    const obj = {};
    set(obj, `${name}`, value);
    // When Product Type changes and cascading is ON, clear all downstream category selections
    if (name === 'selectedProductType' && catBasedOnPTData) {
      obj.selectedCategory1 = [];
      obj.selectedCategory2 = [];
      obj.selectedCategory3 = [];
      obj.selectedCategory4 = [];
    }
    // Reset Category 2 as well when Category 1 changes
    if (name === 'selectedCategory1') {
      obj.selectedCategory2 = [];
    }
    if (
      ['selectedCategory1', 'selectedCategory2'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedCategory3 = [];
      obj.selectedCategory4 = [];
    }
    if (
      ['selectedCategory3'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedCategory4 = [];
    }
    // Reset subBrand when brand changes and clear subBrand options until new ones load
    if (
      ['selectedBrand'].some(function (v) {
        return name.indexOf(v) >= 0;
      })
    ) {
      obj.selectedSubBrand = [];
      setState((prev) => ({ ...prev, subBrandOptions: [] }));
    }
    await dispatch(sampleTodoListAction.filterSortData(obj));
  };

  const clearAll = async () => {
    const paramsObj = {
      seasons: [],
      year: [],
      brand: [],
      subBrand: [],
      supplier: [],
      sampleType: [],
      colour: [],
      colorGroupId: [],
      productTypeId: [],
      trackingStatus: [],
      status: defaultSampleStatusIds,
      category1: [],
      category2: [],
      category3: [],
      category4: [],
      licensorId: [],
      licenseId: [],
      likenessId: [],
      sortOrder,
      sortPropertyName,
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'samples',
    };
    const sortinObj = {
      selectedSeason: [],
      selectedYear: [],
      selectedBrand: [],
      selectedSubBrand: [],
      selectedSupplier: [],
      selectedSampleType: [],
      selectedColour: [],
      selectedColourGroup: [],
      selectedProductType: [],
      selectedTrackingStatus: [],
      selectedSampleStatus: defaultSampleStatusIds,
      selectedCategory1: [],
      selectedCategory2: [],
      selectedCategory3: [],
      selectedCategory4: [],
      selectedLicensor: [],
      selectedLicense: [],
      selectedLikeness: [],
      loading: true,
      sortOrder,
      sortPropertyName,
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
    };
    const res = await dispatch(sampleTodoListAction.getSampleRequestsTodoList(paramsObj));
    dispatch(systemMasterAction.clearLicenseData());
    dispatch(systemMasterAction.clearLikenessData());
    localStorage.removeItem('sampleTodoListFilter');

    if (res) {
      await dispatch(sampleTodoListAction.filterSortData(sortinObj));
    }
  };

  //   useEffect(async()=>{
  // let obj = JSON.parse(localStorage.getItem('sampleTodoListFilter'))
  // await dispatch(sampleTodoListAction.filterSortData(obj));

  //   },[])
  const applyFilter = async () => {
    const paramsObj = {
      seasons: selectedSeason,
      year: selectedYear,
      brand: selectedBrand,
      subBrand: selectedSubBrand,
      supplier: selectedSupplier,
      sampleType: selectedSampleType,
      colour: selectedColour,
      colorGroupId: selectedColourGroup,
      // Only send productTypeId key as backend expects (use Redux field)
      productTypeId: Array.isArray(selectedProductType) ? selectedProductType : [],
      trackingStatus: selectedTrackingStatus,
      status: selectedSampleStatus,
      category1: selectedCategory1,
      category2: selectedCategory2,
      category3: selectedCategory3,
      category4: selectedCategory4,
      licensorId: selectedLicensor,
      licenseId: selectedLicense,
      likenessId: selectedLikeness,
      loading: true,
      sortOrder,
      sortPropertyName,
      searchText,
      pageIndex: 1,
      pageSize: Page_Size,
      type: 'samples',
    };

    const paramsObj1 = {
      selectedSeason,
      selectedYear,
      selectedBrand,
      selectedSubBrand,
      selectedSupplier,
      selectedSampleType,
      selectedColour,
      selectedColourGroup,
      selectedProductType: Array.isArray(selectedProductType) ? selectedProductType : [],
      selectedTrackingStatus,
      selectedSampleStatus,
      selectedCategory1,
      selectedCategory2,
      selectedCategory3,
      selectedCategory4,
      selectedLicensor,
      selectedLicense,
      selectedLikeness,
    };

    localStorage.setItem('sampleTodoListFilter', JSON.stringify(paramsObj1));
    await dispatch(sampleTodoListAction.getSampleRequestsTodoList(paramsObj));
    const obj = {
      pageIndex: 1,
      pageSize: Page_Size,
    };
    await dispatch(sampleTodoListAction.filterSortData(obj));
  };

  const fetchCategories = async () => {
    // Always hydrate Category 1 and 2 base lists
    for (const cat of [1, 2]) {
      const res = await dispatch(
        sampleTodoListAction.fetchCategoryAction({
          categoryType: cat,
          loggedInUserId: userId,
        }),
      );
      const list = Array.isArray(res?.data) ? res.data : res;
      setState((prevState) => ({ ...prevState, [`category${cat}Options`]: createOptions(list, 'name', '_id') }));
    }

    // Hydrate Category 3 if Cat1+Cat2 are already selected (support multi C1×C2)
    if (Array.isArray(selectedCategory1) && selectedCategory1.length && Array.isArray(selectedCategory2) && selectedCategory2.length) {
      const c1s = selectedCategory1;
      const c2s = selectedCategory2;
      const calls3 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          calls3.push(
            dispatch(
              sampleTodoListAction.fetchCategoryAction({
                categoryType: 3,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
              }),
            ),
          );
        });
      });
      const results3 = await Promise.all(calls3);
      const merged3 = [];
      const seen3 = new Set();
      results3.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen3.has(key)) {
              seen3.add(key);
              merged3.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category3Options: createOptions(merged3, 'name', '_id') }));
    }

    // Hydrate Category 4 if Cat1+Cat2+Cat3 are already selected (support multi C1×C2×C3)
    if (
      Array.isArray(selectedCategory1) &&
      selectedCategory1.length &&
      Array.isArray(selectedCategory2) &&
      selectedCategory2.length &&
      Array.isArray(selectedCategory3) &&
      selectedCategory3.length
    ) {
      const c1s = selectedCategory1;
      const c2s = selectedCategory2;
      const c3s = selectedCategory3;
      const calls4 = [];
      c1s.forEach((c1) => {
        c2s.forEach((c2) => {
          c3s.forEach((c3) => {
            calls4.push(
              dispatch(
                sampleTodoListAction.fetchCategoryAction({
                  categoryType: 4,
                  loggedInUserId: userId,
                  categoryOneId: c1,
                  categoryTwoId: c2,
                  categoryThreeId: c3,
                }),
              ),
            );
          });
        });
      });
      const results4 = await Promise.all(calls4);
      const merged4 = [];
      const seen4 = new Set();
      results4.forEach((res) => {
        const arr = Array.isArray(res?.data) ? res.data : res;
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            const key = String(item?._id ?? item?.value ?? '');
            if (key && !seen4.has(key)) {
              seen4.add(key);
              merged4.push(item);
            }
          });
        }
      });
      setState((prevState) => ({ ...prevState, category4Options: createOptions(merged4, 'name', '_id') }));
    }
  };
  // all useEffect
  // call all api's for filter section to fill the dropdown list
  useEffect(() => {
    const callFn = async () => {
      const sesonres = await dispatch(sampleTodoListAction.getSeasonsAction({ loggedInUserId: userId }));
      const BrandRes = isSupplierLoggedIn
        ? await dispatch(getSupplierBrandsAction(userId))
        : await dispatch(
            sampleTodoListAction.fetchAllBrandAction({
              loggedInUserId: userId,
            }),
          );
      if (!isSupplierLoggedIn) {
        let supp_list = [];

        if (isCompanyUserLoggedIn) {
          supp_list = await dispatch(sampleTodoListAction.getUserSuppliers());

          supp_list = supp_list.suppliers;
          supp_list = supp_list.length
            ? supp_list.map((ele) => {
                if (ele.companies.length) {
                  ele.name = ele.companies[0].supplierName;
                }
                return ele;
              })
            : [];
        } else {
          supp_list = await dispatch(
            sampleTodoListAction.getSuppliers({
              loggedInUserId: userId,
            }),
          );
        }

        if (checkArray(supp_list)) {
          const supplierOptionsData = createOptions(supp_list, 'name', '_id');
          setState((prevState) => ({
            ...prevState,
            supplierOptions: supplierOptionsData,
          }));
        }
      }

      const mainSample = await dispatch(
        sampleTodoListAction.getMainSamples({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const otherSample = await dispatch(
        sampleTodoListAction.getOtherSamples({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const colourWaysList = await dispatch(fetchColorLibrary({ loggedInUserId: userId }));
      await dispatch(fetchColorGroupLibrary({ loggedInUserId: userId, isDetails: true }));
      const sampleStatus = await dispatch(
        sampleTodoListAction.getSampleStatus({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const sampleTraking = await dispatch(sampleTodoListAction.getSampleTraking());
      await fetchCategories();
      if (checkArray(sampleTraking)) {
        const trackingStatusOptionsData = createOptions(sampleTraking, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          trackingStatusOptions: trackingStatusOptionsData,
        }));
      }
      if (checkArray(colourWaysList)) {
        const colourOptionsData = createOptions(colourWaysList, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          colourOptions: colourOptionsData,
        }));
      }
      if (checkArray(sampleStatus)) {
        const sampleStatusOptionsData = createOptions(sampleStatus, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          sampleStatusOptions: sampleStatusOptionsData,
        }));
      }
      if (checkArray(mainSample) || checkArray(otherSample)) {
        const mergeSamples = concat(mainSample, otherSample);
        const sampleTypeOptionsData = createOptions(mergeSamples, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          sampleTypeOptions: sampleTypeOptionsData,
        }));
      }

      if (checkArray(sesonres)) {
        const seasonOptionsData = createOptions(sesonres, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          seasonOptions: seasonOptionsData,
        }));
      }
      if (checkArray(BrandRes)) {
        const brandOptionsData = createOptions(BrandRes, 'name', '_id');
        setState((prevState) => ({
          ...prevState,
          brandOptions: brandOptionsData,
        }));
      }
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId]);

  useEffect(() => {
    if (checkArray(colorLibList)) {
      const colorGroupOptionsData = createOptions(colorLibList, 'name', '_id');
      setState((prevState) => ({
        ...prevState,
        colorGroupOptions: colorGroupOptionsData,
      }));
    }
  }, [colorLibList]);

  useEffect(() => {
    const callFn = async () => {
      if (isFetchingCatsRef.current) return;
      isFetchingCatsRef.current = true;
      try {
        const c1 = Array.isArray(selectedCategory1) ? selectedCategory1[0] : selectedCategory1;
        const c2 = Array.isArray(selectedCategory2) ? selectedCategory2[0] : selectedCategory2;
        const c3 = Array.isArray(selectedCategory3) ? selectedCategory3[0] : selectedCategory3;

        if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && checkArray(selectedCategory3) && !isEmpty(userId)) {
          const cat3Ids = Array.isArray(selectedCategory3) ? selectedCategory3 : [selectedCategory3];
          if (Array.isArray(cat3Ids) && cat3Ids.length > 1) {
            const results = await Promise.all(
              cat3Ids.map((id) =>
                dispatch(
                  sampleTodoListAction.fetchCategoryAction({
                    categoryType: 4,
                    loggedInUserId: userId,
                    categoryOneId: c1,
                    categoryTwoId: c2,
                    categoryThreeId: id,
                    isActive: 1,
                  }),
                ),
              ),
            );
            const merged = [];
            const seen = new Set();
            results.forEach((res) => {
              const arr = Array.isArray(res?.data) ? res.data : res;
              if (Array.isArray(arr)) {
                arr.forEach((item) => {
                  const key = String(item?._id ?? item?.value ?? '');
                  if (key && !seen.has(key)) {
                    seen.add(key);
                    merged.push(item);
                  }
                });
              }
            });
            setState((prevState) => ({ ...prevState, category4Options: createOptions(merged, 'name', '_id') }));
          } else {
            const res4 = await dispatch(
              sampleTodoListAction.fetchCategoryAction({
                categoryType: 4,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
                categoryThreeId: c3,
                isActive: 1,
              }),
            );
            const list4 = Array.isArray(res4?.data) ? res4.data : res4;
            setState((prevState) => ({ ...prevState, category4Options: createOptions(list4, 'name', '_id') }));
          }

          // hydrate Cat3 if empty
          if (!Array.isArray(category3Options) || category3Options.length === 0) {
            const res3 = await dispatch(
              sampleTodoListAction.fetchCategoryAction({
                categoryType: 3,
                loggedInUserId: userId,
                categoryOneId: c1,
                categoryTwoId: c2,
                isActive: 1,
              }),
            );
            const list3 = Array.isArray(res3?.data) ? res3.data : res3;
            setState((prevState) => ({ ...prevState, category3Options: createOptions(list3, 'name', '_id') }));
          }
        } else if (checkArray(selectedCategory1) && checkArray(selectedCategory2) && !isEmpty(userId)) {
          // Fetch Category 3 for every C1×C2 combination and merge
          const c1s = selectedCategory1;
          const c2s = selectedCategory2;
          const calls = [];
          c1s.forEach((c1id) => {
            c2s.forEach((c2id) => {
              calls.push(
                dispatch(
                  sampleTodoListAction.fetchCategoryAction({
                    categoryType: 3,
                    loggedInUserId: userId,
                    categoryOneId: c1id,
                    categoryTwoId: c2id,
                    isActive: 1,
                  }),
                ),
              );
            });
          });
          const results = await Promise.all(calls);
          const merged = [];
          const seen = new Set();
          results.forEach((res) => {
            const arr = Array.isArray(res?.data) ? res.data : res;
            if (Array.isArray(arr)) {
              arr.forEach((item) => {
                const key = String(item?._id ?? item?.value ?? '');
                if (key && !seen.has(key)) {
                  seen.add(key);
                  merged.push(item);
                }
              });
            }
          });
          setState((prevState) => ({ ...prevState, category3Options: createOptions(merged, 'name', '_id') }));
        }
      } finally {
        isFetchingCatsRef.current = false;
      }
    };
    callFn();
  }, [selectedCategory1, selectedCategory2, selectedCategory3, dispatch, userId]);
  return (
    <button
      className={isApply ? 'filter-btn filter-button_delete active ' : 'filter-btn filter-button_delete '}
      type="button"
      title={t('FILE_TODO_LIST.SAMPLE_TO_DO_FILTER')}
    >
      <OverlayTrigger
        placement={t('FILE_TODO_LIST.BUTTON')}
        trigger="click"
        rootClose
        overlay={
          <Popover className="custom-popover horizontal-view">
            <Popover.Content>
              <div className="product-costing-filter">
                <div className="filter-text">
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="season"
                        options={seasonOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={seasonOptions.filter((val) => !isEmpty(selectedSeason) && selectedSeason.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SEASON'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SEASON')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSeason',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="year"
                        ItemRenderer={CurrentYearItemRenderer}
                        options={yearOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={yearOptions.filter((val) => !isEmpty(selectedYear) && selectedYear.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_YEAR'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_YEAR')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedYear',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="brand"
                        options={brandOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={brandOptions.filter((val) => !isEmpty(selectedBrand) && selectedBrand.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_BRAND'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_BRAND')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedBrand',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {subBrandAccess && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="subBrand"
                          options={subBrandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={subBrandOptions.filter((val) => !isEmpty(selectedSubBrand) && selectedSubBrand.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: tReports('TODO_COMMON.SELECT_SUBBRAND'),
                          }}
                          labelledBy={tReports('TODO_COMMON.SELECT_SUBBRAND')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selectedSubBrand',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  {!isSupplierLoggedIn && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="supplier"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={supplierOptions.filter((val) => !isEmpty(selectedSupplier) && selectedSupplier.includes(val._id))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SUPLIERS'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_SUPLIERS')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val._id),
                              'selectedSupplier',
                            )
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="sample type"
                        options={sampleTypeOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={sampleTypeOptions.filter((val) => !isEmpty(selectedSampleType) && selectedSampleType.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SAMPLE_TYPE'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SAMPLE_TYPE')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSampleType',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="colorway"
                        options={colourOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={colourOptions.filter((val) => !isEmpty(selectedColour) && selectedColour.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COLOR_LIBRARY.FILTER.SELECT_COLOR_NAME'),
                        }}
                        labelledBy={t('COLOR_LIBRARY.FILTER.SELECT_COLOR_NAME')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedColour',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="colourGroup"
                        options={colorGroupOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={colorGroupOptions.filter((val) => !isEmpty(selectedColourGroup) && selectedColourGroup.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COLOR_LIBRARY.FILTER.SELECT_COLOR_GROUP'),
                        }}
                        labelledBy={t('COLOR_LIBRARY.FILTER.SELECT_COLOR_GROUP')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedColourGroup',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="sample status"
                        options={sampleStatusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={sampleStatusOptions.filter((val) => !isEmpty(selectedSampleStatus) && selectedSampleStatus.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_SAMPLE_STATUS'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_SAMPLE_STATUS')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedSampleStatus',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="tracking-status"
                        options={trackingStatusOptions}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={trackingStatusOptions.filter((val) => !isEmpty(selectedTrackingStatus) && selectedTrackingStatus.includes(val._id))}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_TRACKING_STATUS'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_TRACKING_STATUS')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedTrackingStatus',
                          )
                        }
                      />
                    </div>
                  </FormGroup>{' '}
                  {catBasedOnPTData && (
                    <FormGroup className="mb-2">
                      <div className="custom-react-select ">
                        <MultiSelect
                          hasSelectAll
                          name="productType"
                          options={productTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          value={productTypeOptions.filter((opt) => Array.isArray(selectedProductType) && selectedProductType.includes(opt.value))}
                          overrideStrings={{
                            selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                          }}
                          labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                          onChange={(selected) =>
                            handleSelectChange(Array.isArray(selected) ? selected.map((opt) => opt?.value).filter(Boolean) : [], 'selectedProductType')
                          }
                        />
                      </div>
                    </FormGroup>
                  )}
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category1"
                        options={catBasedOnPTData ? category1Filtered : category1Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category1Filtered : category1Options).filter(
                          (val) => !isEmpty(selectedCategory1) && selectedCategory1.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_1')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedCategory1',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category2"
                        options={catBasedOnPTData ? category2Filtered : category2Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category2Filtered : category2Options).filter(
                          (val) => !isEmpty(selectedCategory2) && selectedCategory2.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_2')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedCategory2',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category3"
                        options={catBasedOnPTData ? category3Filtered : category3Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category3Filtered : category3Options).filter(
                          (val) => !isEmpty(selectedCategory3) && selectedCategory3.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER'),
                        }}
                        labelledBy={t('SPEC_TEMP_LIBRARY.CATEGORY3_PLACEHOLDER')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedCategory3',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  <FormGroup className="mb-2">
                    <div className="custom-react-select ">
                      <MultiSelect
                        hasSelectAll
                        name="category4"
                        options={catBasedOnPTData ? category4Filtered : category4Options}
                        className="product-custom-multiselect common-new-multiselect w-100"
                        value={(catBasedOnPTData ? category4Filtered : category4Options).filter(
                          (val) => !isEmpty(selectedCategory4) && selectedCategory4.includes(val._id),
                        )}
                        overrideStrings={{
                          selectSomeItems: t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4'),
                        }}
                        labelledBy={t('COSTING_TO_DO_LIST.SELECT_CATEGORY_4')}
                        onChange={(selected) =>
                          handleSelectChange(
                            selected.map((val) => val._id),
                            'selectedCategory4',
                          )
                        }
                      />
                    </div>
                  </FormGroup>
                  {licensorAccess && (
                    <>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="licensor"
                            options={licensorOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licensorOptions.filter((val) => checkArray(selectedLicensor) && selectedLicensor.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSOR'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSOR')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicensor',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="license"
                            options={licenseOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={licenseOptions.filter((val) => checkArray(selectedLicense) && selectedLicense.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LICENSE'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LICENSE')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLicense',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                      <FormGroup className="mb-2">
                        <div className="custom-react-select">
                          <MultiSelect
                            hasSelectAll
                            name="likeness"
                            options={likenessOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            value={likenessOptions.filter((val) => checkArray(selectedLikeness) && selectedLikeness.includes(val._id))}
                            overrideStrings={{
                              selectSomeItems: tReports('LICENCES.SELECT_LIKENESS'),
                            }}
                            labelledBy={tReports('LICENCES.SELECT_LIKENESS')}
                            onChange={(selected) =>
                              handleFilterLicense(
                                selected.map((val) => val._id),
                                'selectedLikeness',
                              )
                            }
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                </div>

                <div className="d-flex justify-content-end">
                  <button type="button" aria-label="Clear" className="btn p-0 d-inline-flex" onClick={clearAll}>
                    <span className="icon-Clear-New f-24" />
                  </button>
                  <button type="button" aria-label="Apply" className="btn p-0 d-inline-flex ml-16" onClick={applyFilter}>
                    <span className="icon-Tick-1 f-28" />
                  </button>
                </div>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <i className="icon-Filter f-26" />
      </OverlayTrigger>
    </button>
  );
}

export default FilterSampleTodoList;
